// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
export var EncrTypes;
(function (EncrTypes) {
    EncrTypes[EncrTypes["STANDART"] = 0] = "STANDART";
    EncrTypes[EncrTypes["AES_GCM_NoPadding"] = 2] = "AES_GCM_NoPadding";
})(EncrTypes || (EncrTypes = {}));
// Due to security restrictions, the SubtleCrypto API (crypto.subtle) is not available in non-secure contexts such as HTTP.
// As a fallback mechanism for environments where crypto.subtle is unavailable,
// we employ simplified encryption methods.
// For more information on this limitation, please refer to the warning issued by MDN Web Docs:
// https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto
function makeKey(key) {
    return __awaiter(this, void 0, void 0, function* () {
        const enc = new TextEncoder().encode(key);
        if (crypto.subtle != null) {
            return yield crypto.subtle.importKey('raw', enc, 'AES-GCM', false, // whether the key is extractable (i.e. can be used in exportKey)
            ['encrypt', 'decrypt'] // can "encrypt", "decrypt", "wrapKey", or "unwrapKey"
            );
        }
        else {
            return key; // Fallback: Return the key itself
        }
    });
}
export function EncodeKeyAesGcmNoPadding(string, key) {
    return __awaiter(this, void 0, void 0, function* () {
        const keyNew = yield makeKey(key);
        if (crypto.subtle != null && typeof keyNew !== 'string') {
            const encrypted = yield encrypt(string, keyNew);
            return btoa(String.fromCharCode(...new Uint8Array(encrypted.encrypted)));
        }
        else {
            // Fallback: Use a simplified encryption method
            return btoa(simpleEncrypt(string, key));
        }
    });
}
class _ProXOREncryptionUtility {
    Encode(dataInput, keyInput) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield new Promise((resolve, reject) => {
                const data = ProXOREncryptionUtility.GetBytes(dataInput);
                const key = ProXOREncryptionUtility.GetBytes(keyInput);
                const cipher = new Array(data.length);
                const realKey = new Array(data.length);
                const mask = [
                    0x49, 0x32, 0x76, 0x61, 0x6e, 0x20, 0x11, 0x4d, 0x65, 0x64, 0x76, 0x65, 0x64, 0x65, 0x60, 0x76
                ];
                for (let i = 0; i < realKey.length; i++) {
                    realKey[i] = key[i % key.length] ^ mask[i % mask.length];
                }
                for (let i = 0; i < data.length; i++) {
                    cipher[i] = data[i] ^ realKey[i];
                }
                const u8 = new Uint8Array(cipher);
                resolve(btoa(String.fromCharCode.apply(null, u8)));
            });
        });
    }
    GetBytes(data) {
        const encoder = new TextEncoder();
        const encoded = encoder.encode(data);
        return Array.from(encoded);
    }
}
export const ProXOREncryptionUtility = new _ProXOREncryptionUtility();
export function makeKeyWithDerivation(key) {
    return __awaiter(this, void 0, void 0, function* () {
        if (crypto.subtle != null) {
            // Convert passphrase to an ArrayBuffer using TextEncoder
            const enc = new TextEncoder().encode(key);
            // Derive a key from the passphrase using PBKDF2
            const derivedKey = yield crypto.subtle.importKey('raw', enc, { name: 'PBKDF2' }, false, ['deriveKey']);
            // Derive a key of suitable length (128, 192, or 256 bits) using the derived key and appropriate parameters
            return yield crypto.subtle.deriveKey({
                name: 'PBKDF2',
                salt: new Uint8Array(16), // Generate a random salt of suitable length
                iterations: 100000, // Adjust iterations as per your security requirements
                hash: 'SHA-256' // Use a suitable hash algorithm
            }, derivedKey, { name: 'AES-GCM', length: 256 }, // Ensure the key length is appropriate (128, 192, or 256)
            false, ['encrypt', 'decrypt']);
        }
        else {
            return key; // Fallback: Return the key itself
        }
    });
}
const IV = new Uint8Array([
    0x49, 0x32, 0x76, 0x61, 0x6e, 0x20, 0x11, 0x4d, 0x65, 0x64, 0x76, 0x65, 0x64, 0x65, 0x60, 0x76
]);
export function encrypt(string, key) {
    return __awaiter(this, void 0, void 0, function* () {
        if (crypto.subtle != null && typeof key !== 'string') {
            const encoded = new TextEncoder().encode(string);
            const iv = IV;
            const encrypted = yield crypto.subtle.encrypt({
                name: 'AES-GCM',
                iv
                // tagLength: 128, //can be 32, 64, 96, 104, 112, 120 or 128 (default)
            }, key, // from generateKey or importKey above
            encoded // ArrayBuffer of data you want to encrypt
            );
            return { encrypted, iv };
        }
        else {
            // Fallback: Use a simplified encryption method
            const encrypted = simpleEncrypt(string, key);
            const encoded = new TextEncoder().encode(encrypted);
            return { encrypted: encoded.buffer, iv: IV };
        }
    });
}
export function decrypt(encrypted, key) {
    return __awaiter(this, void 0, void 0, function* () {
        if (crypto.subtle != null && typeof key !== 'string') {
            const iv = IV;
            const decrypted = yield crypto.subtle.decrypt({
                name: 'AES-GCM',
                iv
            }, key, encrypted);
            return new TextDecoder().decode(decrypted);
        }
        else {
            // Fallback: Use a simplified decryption method
            const decoded = new TextDecoder().decode(encrypted);
            return simpleDecrypt(decoded, key);
        }
    });
}
export function encodeArrayBufferToString(buffer) {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)));
}
export function decodeStringToArrayBuffer(str) {
    return new Uint8Array(atob(str)
        .split('')
        .map((char) => char.charCodeAt(0))).buffer;
}
export function encryptAndDeriveKey(string, keyString) {
    return __awaiter(this, void 0, void 0, function* () {
        const key = yield makeKeyWithDerivation(keyString); // Encrypt the password using AES-GCM
        if (crypto.subtle != null && typeof key !== 'string') {
            const { encrypted } = yield encrypt(string, key);
            return encodeArrayBufferToString(encrypted);
        }
        else {
            // Fallback: Use a simplified encryption method
            return btoa(simpleEncrypt(string, key));
        }
    });
}
export function decryptWithDerivedKey(encryptedPassword, keyString) {
    return __awaiter(this, void 0, void 0, function* () {
        const key = yield makeKeyWithDerivation(keyString); // Derive the key using AES-GCM
        if (crypto.subtle != null && typeof key !== 'string') {
            return yield decrypt(decodeStringToArrayBuffer(encryptedPassword), key);
        }
        else {
            // Fallback: Use a simplified decryption method
            return simpleDecrypt(atob(encryptedPassword), key);
        }
    });
}
// Function to generate a key for XOR encryption
function generateXORKey(key) {
    // Reverse the key
    return key.split('').reverse().join('');
}
// Function to encrypt a string using XOR encryption
function simpleEncrypt(string, key) {
    const xorKey = generateXORKey(key);
    let result = '';
    for (let i = 0; i < string.length; i++) {
        const charCode = string.charCodeAt(i) ^ xorKey.charCodeAt(i % xorKey.length);
        result += String.fromCharCode(charCode);
    }
    return result;
}
// Function to decrypt a string encrypted with XOR encryption
function simpleDecrypt(encrypted, key) {
    return simpleEncrypt(encrypted, key); // XOR encryption is its own inverse
}
